import React, { useMemo, useState } from 'react'
import { useMutation, useQuery } from 'urql'
import CustomModal from '../shared/CustomModal/CustomModal'
import {
  CREATE_SIGNIN_TEMPLATE,
  DELETE_SIGNIN_TEMPLATE,
  FORM_SIDES,
  formatFormSide,
  GET_SIGNIN_ASSET_UPLOAD_URL,
  GET_SIGNIN_TEMPLATES,
  SET_DEFAULT_LOGIN,
  SET_SIGNIN_TEMPLATE_ACTIVE,
  UPDATE_SIGNIN_TEMPLATE,
  uploadSigninImage,
} from '../../graphql/signinTemplates'
import './SigninThemesList.css'
import SignInFormPreview from './SignInFormPreview'

// URL del portal (smart_front) donde vive el login público. Configurable por
// entorno; por defecto apunta a producción.
const PORTAL_URL = (process.env.REACT_APP_PORTAL_URL || 'https://portal.smartcfo.cl').replace(/\/$/, '')

const FORM_WIDTH_MIN = 30
const FORM_WIDTH_MAX = 50
const FORM_WIDTH_DEFAULT = 40

const clampFormWidth = (value) => {
  const n = Math.round(Number(value))
  if (!Number.isFinite(n)) return FORM_WIDTH_DEFAULT
  return Math.min(FORM_WIDTH_MAX, Math.max(FORM_WIDTH_MIN, n))
}

const FORM_BG_DEFAULT = '#FAFAFA'
const HEX_COLOR_REGEX = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/

const normalizeBgColor = (value) => {
  const raw = String(value ?? '').trim()
  return HEX_COLOR_REGEX.test(raw) ? raw : FORM_BG_DEFAULT
}

const defaultFormState = () => ({
  name: '',
  html: '',
  formSide: 'LEFT',
  formWidth: FORM_WIDTH_DEFAULT,
  formBgColor: FORM_BG_DEFAULT,
  isActive: false,
})

const buildLiveUrl = (id) => `${PORTAL_URL}/signin?previewTemplate=${encodeURIComponent(id)}`

// El iframe ocupa el 100% de su panel (mismo contrato que el login real, donde
// el HTML llena su columna). Es responsabilidad del HTML adaptarse al ancho de
// su contenedor; así la vista previa no genera scroll ni escalados raros.
function ThemeFrame({ html }) {
  return (
    <div className="signin-theme-preview__frame-wrap">
      <iframe
        title="Vista previa de la plantilla"
        srcDoc={html}
        sandbox="allow-scripts"
        style={{ width: '100%', height: '100%', border: 0, display: 'block' }}
      />
    </div>
  )
}

function SplitPreview({ html, formSide, formWidth, formBgColor }) {
  const side = formSide === 'RIGHT' ? 'form-right' : 'form-left'
  const width = clampFormWidth(formWidth)
  const bg = normalizeBgColor(formBgColor)
  return (
    <div className={`signin-theme-preview ${side}`}>
      <div
        className="signin-theme-preview__form"
        style={{ flex: `0 0 ${width}%`, maxWidth: `${width}%`, background: bg }}
      >
        <SignInFormPreview />
      </div>
      {html?.trim() ? (
        <ThemeFrame html={html} />
      ) : (
        <div className="signin-theme-preview__frame--empty">
          <span>El HTML aparecerá aquí</span>
        </div>
      )}
    </div>
  )
}

function TemplateFormContent({ form, setForm }) {
  const handleFile = (event) => {
    const file = event.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      const content = String(reader.result || '')
      setForm((prev) => ({
        ...prev,
        html: content,
        name: prev.name || file.name.replace(/\.html?$/i, ''),
      }))
    }
    reader.readAsText(file)
    // Permite volver a subir el mismo archivo.
    event.target.value = ''
  }

  return (
    <div className="signin-theme-form signin-theme-form--split">
      <div className="signin-theme-form__col signin-theme-form__col--fields">
        <label className="signin-theme-field">
          <span>Nombre de la plantilla</span>
          <input
            type="text"
            value={form.name}
            onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
            placeholder="Ej: Campaña Renta 2026"
          />
        </label>

        <div className="signin-theme-field">
          <span>Posición del formulario</span>
          <div className="signin-theme-sides signin-theme-sides--compact">
            {FORM_SIDES.map((item) => (
              <label
                key={item.value}
                className={`signin-theme-side ${form.formSide === item.value ? 'selected' : ''}`}
              >
                <input
                  type="radio"
                  name="signin-form-side"
                  checked={form.formSide === item.value}
                  onChange={() => setForm((prev) => ({ ...prev, formSide: item.value }))}
                />
                <span>{item.shortLabel || item.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="signin-theme-field">
          <div className="signin-theme-ratio-head">
            <span>Ancho del formulario</span>
            <span className="signin-theme-ratio-value">
              Formulario {form.formWidth}% · HTML {100 - form.formWidth}%
            </span>
          </div>
          <input
            type="range"
            className="signin-theme-range"
            min={FORM_WIDTH_MIN}
            max={FORM_WIDTH_MAX}
            step={1}
            value={form.formWidth}
            onChange={(event) =>
              setForm((prev) => ({ ...prev, formWidth: clampFormWidth(event.target.value) }))
            }
          />
        </div>

        <div className="signin-theme-field">
          <span>Color de fondo del formulario</span>
          <div className="signin-theme-color">
            <input
              type="color"
              className="signin-theme-color__picker"
              value={normalizeBgColor(form.formBgColor)}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, formBgColor: event.target.value }))
              }
            />
            <input
              type="text"
              className="signin-theme-color__hex"
              value={form.formBgColor}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, formBgColor: event.target.value }))
              }
              placeholder="#FAFAFA"
              spellCheck={false}
            />
            <button
              type="button"
              className="signin-theme-color__swatch"
              style={{ background: '#FFFFFF' }}
              title="Blanco"
              onClick={() => setForm((prev) => ({ ...prev, formBgColor: '#FFFFFF' }))}
            />
            <button
              type="button"
              className="signin-theme-color__swatch"
              style={{ background: FORM_BG_DEFAULT }}
              title="Por defecto (#FAFAFA)"
              onClick={() => setForm((prev) => ({ ...prev, formBgColor: FORM_BG_DEFAULT }))}
            />
          </div>
        </div>

        <div className="signin-theme-field">
          <span>Archivo HTML (.html)</span>
          <input type="file" accept=".html,text/html" onChange={handleFile} />
          {form.html ? (
            <small className="signin-theme-hint signin-theme-hint--ok">
              ✓ HTML cargado ({form.html.length.toLocaleString('es-CL')} caracteres).
            </small>
          ) : null}
          <small className="signin-theme-hint">
            El HTML debe <strong>llenar el 100%</strong> de su panel (ocupa el resto del ancho; el
            formulario usa el porcentaje configurado arriba). No incluyas tu propia columna para el
            formulario ni <code>@media</code> que oculten el contenido. Usa URLs absolutas (o el
            botón "Subir imagen") para las imágenes.
          </small>
        </div>
      </div>

      <div className="signin-theme-form__col signin-theme-form__col--preview">
        <div className="signin-theme-field">
          <span>
            Vista previa (distribución {form.formWidth}% / {100 - form.formWidth}%)
          </span>
          <SplitPreview
            html={form.html}
            formSide={form.formSide}
            formWidth={form.formWidth}
            formBgColor={form.formBgColor}
          />
        </div>
      </div>
    </div>
  )
}

function TemplateActivateContent({ template }) {
  const isDeactivating = Boolean(template?.isActive)
  return (
    <div className="signin-delete-modal-body">
      <div
        className={`signin-confirm-modal-icon ${
          isDeactivating ? 'signin-confirm-modal-icon--warning' : ''
        }`}
        aria-hidden
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
          <line x1="12" y1="2" x2="12" y2="12" />
        </svg>
      </div>

      <p className="signin-delete-modal-lead">
        {isDeactivating ? (
          <>
            ¿Desactivar la plantilla <strong>{template?.name}</strong>?
          </>
        ) : (
          <>
            ¿Usar la plantilla <strong>{template?.name}</strong> en el login?
          </>
        )}
      </p>

      <p className="signin-delete-modal-warning">
        {isDeactivating
          ? 'El login volverá a su apariencia por defecto (formulario centrado) para todos los usuarios.'
          : 'Reemplazará la apariencia del acceso para todos los usuarios de inmediato.'}
      </p>
    </div>
  )
}

function TemplateDeleteContent({ name }) {
  return (
    <div className="signin-delete-modal-body">
      <div className="signin-delete-modal-icon" aria-hidden>
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="3 6 5 6 21 6" />
          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
          <line x1="10" y1="11" x2="10" y2="17" />
          <line x1="14" y1="11" x2="14" y2="17" />
          <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
        </svg>
      </div>

      <p className="signin-delete-modal-lead">
        ¿Seguro que quieres eliminar la plantilla <strong>{name}</strong>?
      </p>

      <p className="signin-delete-modal-warning">Esta acción no se puede deshacer.</p>
    </div>
  )
}

export function SigninThemesList() {
  const [{ data, fetching }, reexecuteQuery] = useQuery({
    query: GET_SIGNIN_TEMPLATES,
    requestPolicy: 'network-only',
  })
  const [, createTemplate] = useMutation(CREATE_SIGNIN_TEMPLATE)
  const [, updateTemplate] = useMutation(UPDATE_SIGNIN_TEMPLATE)
  const [, deleteTemplate] = useMutation(DELETE_SIGNIN_TEMPLATE)
  const [, setTemplateActive] = useMutation(SET_SIGNIN_TEMPLATE_ACTIVE)
  const [, setDefaultLogin] = useMutation(SET_DEFAULT_LOGIN)
  const [, getAssetUploadUrl] = useMutation(GET_SIGNIN_ASSET_UPLOAD_URL)

  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(defaultFormState())
  const [deleteModal, setDeleteModal] = useState({ open: false, template: null })
  const [activateModal, setActivateModal] = useState({ open: false, template: null })
  const [previewModal, setPreviewModal] = useState({ open: false, template: null })
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [busy, setBusy] = useState(false)
  const [feedback, setFeedback] = useState(null)
  const [uploadingImg, setUploadingImg] = useState(false)
  const [uploadedImages, setUploadedImages] = useState([])

  const templates = useMemo(() => data?.signinTemplates || [], [data?.signinTemplates])
  const activeTemplate = useMemo(() => templates.find((t) => t.isActive) || null, [templates])

  const showFeedback = (type, message) => {
    setFeedback({ type, message })
    window.setTimeout(() => setFeedback(null), 3500)
  }

  const openCreateModal = () => {
    setEditing(null)
    setForm(defaultFormState())
    setModalOpen(true)
  }

  const openEditModal = (template) => {
    setEditing(template)
    setForm({
      name: template.name || '',
      html: template.html || '',
      formSide: template.formSide || 'LEFT',
      formWidth: clampFormWidth(template.formWidth ?? FORM_WIDTH_DEFAULT),
      formBgColor: normalizeBgColor(template.formBgColor),
      isActive: Boolean(template.isActive),
    })
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setEditing(null)
    setForm(defaultFormState())
  }

  const handleSave = async () => {
    if (!form.name.trim()) {
      showFeedback('error', 'El nombre es obligatorio.')
      return
    }
    if (!form.html.trim()) {
      showFeedback('error', 'Debes subir o pegar el HTML.')
      return
    }

    setSaving(true)
    try {
      const base = {
        name: form.name.trim(),
        html: form.html,
        formSide: form.formSide,
        formWidth: clampFormWidth(form.formWidth),
        formBgColor: normalizeBgColor(form.formBgColor),
        isActive: Boolean(form.isActive),
      }
      const result = editing?.id
        ? await updateTemplate({ input: { id: editing.id, ...base } })
        : await createTemplate({ input: base })

      if (result.error) throw result.error
      showFeedback('success', editing ? 'Plantilla actualizada.' : 'Plantilla creada.')
      closeModal()
      reexecuteQuery({ requestPolicy: 'network-only' })
    } catch (error) {
      showFeedback('error', error?.message || 'No se pudo guardar la plantilla.')
    } finally {
      setSaving(false)
    }
  }

  const handleActivate = async () => {
    const template = activateModal.template
    if (!template?.id) return
    setBusy(true)
    try {
      const result = await setTemplateActive({ id: template.id, isActive: !template.isActive })
      if (result.error) throw result.error
      showFeedback('success', template.isActive ? 'Plantilla desactivada.' : 'Plantilla activada.')
      setActivateModal({ open: false, template: null })
      reexecuteQuery({ requestPolicy: 'network-only' })
    } catch (error) {
      showFeedback('error', error?.message || 'No se pudo cambiar el estado.')
    } finally {
      setBusy(false)
    }
  }

  const handleDefaultLogin = async () => {
    setBusy(true)
    try {
      const result = await setDefaultLogin({})
      if (result.error) throw result.error
      showFeedback('success', 'El login volvió a su apariencia por defecto (centrado).')
      reexecuteQuery({ requestPolicy: 'network-only' })
    } catch (error) {
      showFeedback('error', error?.message || 'No se pudo volver al login por defecto.')
    } finally {
      setBusy(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteModal.template?.id) return
    setDeleting(true)
    try {
      const result = await deleteTemplate({ id: deleteModal.template.id })
      if (result.error) throw result.error
      showFeedback('success', 'Plantilla eliminada.')
      setDeleteModal({ open: false, template: null })
      reexecuteQuery({ requestPolicy: 'network-only' })
    } catch (error) {
      showFeedback('error', error?.message || 'No se pudo eliminar la plantilla.')
    } finally {
      setDeleting(false)
    }
  }

  const openLivePreview = (template) => {
    window.open(buildLiveUrl(template.id), '_blank', 'noopener,noreferrer')
  }

  const handleUploadImage = async (event) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    setUploadingImg(true)
    try {
      const url = await uploadSigninImage(file, getAssetUploadUrl)
      setUploadedImages((prev) => [{ name: file.name, url }, ...prev])
      showFeedback('success', 'Imagen subida. Copia la URL y pégala en tu HTML.')
    } catch (error) {
      showFeedback('error', error?.message || 'No se pudo subir la imagen.')
    } finally {
      setUploadingImg(false)
    }
  }

  const handleCopyUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url)
      showFeedback('success', 'URL copiada al portapapeles.')
    } catch (error) {
      showFeedback('error', 'No se pudo copiar. Copia la URL manualmente.')
    }
  }

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Apariencia del Login</h1>
        <p className="page-subtitle">
          Gestiona el diseño de la página de acceso. El formulario ocupa el ancho configurado (40% por defecto) y el HTML, el resto.
        </p>
      </div>

      {feedback ? <div className={`alert ${feedback.type}`}>{feedback.message}</div> : null}

      <div className="content-card signin-theme-status">
        <div className="card-body">
          <div className="signin-theme-status__row">
            <div>
              <span className="signin-theme-status__label">Estado actual del login</span>
              {activeTemplate ? (
                <p className="signin-theme-status__value">
                  Plantilla activa: <strong>{activeTemplate.name}</strong>{' '}
                  ({formatFormSide(activeTemplate.formSide)})
                </p>
              ) : (
                <p className="signin-theme-status__value">
                  Login por defecto (formulario centrado, sin HTML).
                </p>
              )}
            </div>
            <button
              type="button"
              className="btn btn-outline-warning"
              onClick={handleDefaultLogin}
              disabled={busy || !activeTemplate}
              title={!activeTemplate ? 'Ya estás usando el login por defecto' : undefined}
            >
              Volver al login por defecto (centrado)
            </button>
          </div>
        </div>
      </div>

      <div className="content-card">
        <div className="card-header">
          <h3 className="card-title">Imágenes del login</h3>
          <label className={`btn btn-secondary signin-theme-upload-btn ${uploadingImg ? 'is-disabled' : ''}`}>
            {uploadingImg ? 'Subiendo…' : '+ Subir imagen'}
            <input
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
              onChange={handleUploadImage}
              disabled={uploadingImg}
              hidden
            />
          </label>
        </div>
        <div className="card-body">
          <p className="signin-theme-hint">
            Sube aquí las imágenes que usarás en tu HTML. Copia la URL pública y pégala en el
            atributo <code>src</code> de cada <code>&lt;img&gt;</code>.
          </p>
          {uploadedImages.length > 0 ? (
            <ul className="signin-theme-uploads">
              {uploadedImages.map((img) => (
                <li key={img.url} className="signin-theme-upload">
                  <img src={img.url} alt={img.name} className="signin-theme-upload__thumb" />
                  <input className="signin-theme-upload__url" value={img.url} readOnly />
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => handleCopyUrl(img.url)}
                  >
                    Copiar URL
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>

      <div className="content-card">
        <div className="card-header">
          <h3 className="card-title">Plantillas guardadas</h3>
          <button type="button" className="btn btn-primary" onClick={openCreateModal}>
            + Nueva plantilla
          </button>
        </div>
        <div className="card-body">
          {fetching ? (
            <div className="loading-spinner">
              <div className="spinner" />
            </div>
          ) : templates.length === 0 ? (
            <div className="empty-state">
              <p>No hay plantillas guardadas. Crea la primera para personalizar el login.</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>NOMBRE</th>
                  <th>POSICIÓN</th>
                  <th>ESTADO</th>
                  <th>ACTUALIZADA</th>
                  <th>ACCIONES</th>
                </tr>
              </thead>
              <tbody>
                {templates.map((template) => (
                  <tr key={template.id}>
                    <td>{template.name}</td>
                    <td>{formatFormSide(template.formSide)}</td>
                    <td>
                      <span className={`status-badge ${template.isActive ? 'activo' : 'desactivado'}`}>
                        {template.isActive ? 'Activa' : 'Inactiva'}
                      </span>
                    </td>
                    <td>
                      {template.updatedAt
                        ? new Date(template.updatedAt).toLocaleString('es-CL')
                        : '—'}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => setPreviewModal({ open: true, template })}
                        >
                          Previsualizar
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => openLivePreview(template)}
                        >
                          Ver en vivo
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => openEditModal(template)}
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          className={`btn btn-sm ${template.isActive ? 'btn-outline-warning' : 'btn-outline-success'}`}
                          onClick={() => setActivateModal({ open: true, template })}
                          disabled={busy}
                        >
                          {template.isActive ? 'Desactivar' : 'Usar esta'}
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => setDeleteModal({ open: true, template })}
                        >
                          Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <CustomModal
        open={modalOpen}
        onClose={closeModal}
        title={editing ? 'Editar plantilla' : 'Nueva plantilla'}
        size="large"
        classNameModal="signin-theme-modal"
        classNameContent="scfo-modal-form-body"
        content={<TemplateFormContent form={form} setForm={setForm} />}
        onConfirm={handleSave}
        confirmLabel={editing ? 'Guardar cambios' : 'Crear plantilla'}
        confirmDisabled={saving}
        isLoading={saving}
      />

      <CustomModal
        open={previewModal.open}
        onClose={() => setPreviewModal({ open: false, template: null })}
        title={`Vista previa — ${previewModal.template?.name || ''}`}
        size="large"
        classNameModal="signin-theme-modal"
        classNameContent="signin-theme-preview-modal"
        hideActions
        content={
          previewModal.template ? (
            <SplitPreview
              html={previewModal.template.html}
              formSide={previewModal.template.formSide}
              formWidth={previewModal.template.formWidth}
              formBgColor={previewModal.template.formBgColor}
            />
          ) : null
        }
      />

      <CustomModal
        open={activateModal.open}
        onClose={() => setActivateModal({ open: false, template: null })}
        title={activateModal.template?.isActive ? 'Desactivar plantilla' : 'Usar esta plantilla'}
        size="tiny"
        classNameModal="signin-delete-modal"
        content={<TemplateActivateContent template={activateModal.template} />}
        onConfirm={handleActivate}
        confirmLabel={activateModal.template?.isActive ? 'Desactivar' : 'Usar esta'}
        confirmVariant={activateModal.template?.isActive ? 'danger' : 'primary'}
        confirmDisabled={busy}
        isLoading={busy}
      />

      <CustomModal
        open={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, template: null })}
        title="Eliminar plantilla"
        size="tiny"
        classNameModal="signin-delete-modal"
        content={<TemplateDeleteContent name={deleteModal.template?.name} />}
        onConfirm={handleDelete}
        confirmLabel="Eliminar"
        confirmVariant="danger"
        confirmDisabled={deleting}
        isLoading={deleting}
      />
    </>
  )
}

export default SigninThemesList
